import { Injectable } from "@angular/core";
import * as qz from "qz-tray";
import { ImpressaoEventService } from "../events/impressao-event.service";
import { DriverEscPos } from "../escpos/driverEscPos";
import { ApiService, EventoConfirmacaoPedido } from "./api.service";

export type ModoDisparoImpressao = "MANUAL" | "AUTOMATICO";

export interface ConfiguracaoImpressao {
  impressora: string;
  numeroCopias: number;
  disparoImpressao: ModoDisparoImpressao;
  numeroColunas: number;
  avancoPapelFinal?: string;
}

@Injectable({
  providedIn: "root",
})
export class ImpressaoService {
  statusImpressora: "ATIVA" | "INATIVA" = "INATIVA";

  statusDriver: "ATIVO" | "INATIVO" = "INATIVO";

  assinaturaCertificado: "ATIVO" | "INATIVO" = "INATIVO";

  listaImpressoras: string[] = [];

  private _configuracao: ConfiguracaoImpressao;

  erro = { message: "" };

  constructor(
    private impressaoEventService: ImpressaoEventService,
    private apiService: ApiService
  ) {
    this.restaurarConfiguracaoSalvaNoLocalStorage();

    if (this.impressoraFoiDefinida) {
      this.start();
    }

    this.apiService.onConfirmacaoPedido$.subscribe(
      (result: EventoConfirmacaoPedido) => {
        if (this.impressoraEstaInativa) {
          return;
        }

        this.apiService
          .getImpressaoPedidoEscPosByHash(
            result.Hash,
            result.ArrayHash,
            this.configuracao?.numeroColunas ?? 42
          )
          .subscribe((resposta) => {
            if (!this.impressoraEstaAtiva) return;

            if (this.configuracao?.disparoImpressao !== "AUTOMATICO") return;

            if (resposta.status === "success") {
              if (!resposta.data?.length) return;

              const numeroCopias = this.configuracao?.numeroCopias ?? 1;

              for (const impressao of resposta.data) {
                if (!impressao) continue;

                for (let i = 0; i < numeroCopias; i++) {
                  setTimeout(() => {
                    this.imprimirEscPos(impressao);
                  }, 1000 * i);
                }
              }
            }
          });
      }
    );
  }

  get configuracao(): ConfiguracaoImpressao {
    return this._configuracao;
  }

  set configuracao(value: ConfiguracaoImpressao) {
    this._configuracao = value;
  }

  get driverEstaAtivo() {
    return this.statusDriver === "ATIVO";
  }

  get impressoraFoiDefinida() {
    return !!this.configuracao?.impressora;
  }

  get impressoraDefinidadeEhValida() {
    return (
      this.listaImpressoras.includes(this.configuracao?.impressora) ?? false
    );
  }

  get impressoraEstaAtiva() {
    return (
      this.driverEstaAtivo &&
      this.impressoraFoiDefinida &&
      this.impressoraDefinidadeEhValida
    );
  }

  get impressoraEstaInativa() {
    return !this.impressoraEstaAtiva;
  }

  get modoImpressaoManual() {
    return this.configuracao?.disparoImpressao === "MANUAL";
  }

  get temCertificado() {
    return !!this.getCertificado();
  }

  get temChavePrivada() {
    return !!this.getChaveCertificado();
  }

  get numeroColunas(): number {
    return !!this._configuracao?.numeroColunas
      ? Number(this._configuracao.numeroColunas)
      : 42;
  }

  setCertificado(value: string) {
    localStorage.setItem("certificate", value);
  }

  setChaveCertificado(value: string) {
    localStorage.setItem("private-key", value);
  }

  getCertificado() {
    const data = localStorage.getItem("certificate");
    if (!data) return null;
    return data;
  }

  getChaveCertificado() {
    const data = localStorage.getItem("private-key");
    if (!data) return null;
    return data;
  }

  restaurarConfiguracaoSalvaNoLocalStorage() {
    const configuracao = localStorage.getItem("configuracao-impressao");
    if (!configuracao) {
      return;
    }

    try {
      const configuracaoJSON = JSON.parse(configuracao);
      this.configuracao = configuracaoJSON;
    } catch (e) {
      return;
    }
  }

  salvarConfiguracao(dados: ConfiguracaoImpressao) {
    this.configuracao = dados;
    localStorage.setItem("configuracao-impressao", JSON.stringify(dados));

    if (this.impressoraFoiDefinida && !this.driverEstaAtivo) {
      this.start();
    }
  }

  start() {
    const privateKey = this.getChaveCertificado();
    const certificado = this.getCertificado();

    if (!!privateKey && !!certificado) {
      qz.security.setCertificatePromise(function (resolve: any) {
        resolve(certificado);
      });

      qz.security.setSignaturePromise(function (toSign) {
        return function (resolve, reject) {
          try {
            var pk = KEYUTIL.getKey(privateKey);
            var sig = new KJUR.crypto.Signature({ alg: "SHA1withRSA" }); // Use "SHA1withRSA" for QZ Tray 2.0 and older
            sig.init(pk);
            sig.updateString(toSign);
            var hex = sig.sign();
            resolve(stob64(hextorstr(hex)));
          } catch (err) {
            console.error(err);
            reject(err);
          }
        };
      });
    }

    qz.websocket
      .connect()
      .then(() => {
        this.statusDriver = "ATIVO";
        return qz.printers.find();
      })
      .then((printers: string[]) => {
        this.impressaoEventService.onImpressorasListadas$.next(printers);
        this.listaImpressoras = printers;
      }) //.then(() => {
      //   return qz.websocket.disconnect();
      // }).then(() => {
      //   // process.exit(0);
      // })
      .catch((error: any) => {
        console.error(error);
        // process.exit(1);
      });
  }

  listarImpressoras() {}

  imprimirPDF(data: any, retornarErro: boolean = true): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.impressoraEstaAtiva && !retornarErro) {
        resolve(false);
        return;
      }

      if (!this.impressoraEstaAtiva && retornarErro) {
        reject("Impressora inativa");
        return;
      }

      const config = qz.configs.create("PDF");
      return qz
        .print(config, [
          {
            type: "pixel",
            format: "html",
            flavor: "plain",
            data: "<h1>Hello JavaScript!</h1>",
          },
        ])
        .then(() => {
          resolve(true);
        })
        .catch((error: any) => {
          if (retornarErro) {
            reject(error);
          }
        });
    });
  }

  async imprimirEscPos(
    html: string,
    retornarErro: boolean = true
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.impressoraEstaAtiva && !retornarErro) {
        resolve(false);
        return;
      }

      if (!this.impressoraEstaAtiva && retornarErro) {
        reject("Impressora inativa");
        return;
      }

      var config = qz.configs.create(this.configuracao.impressora);

      const numeroAvancoPapel =
        Number(this?.configuracao?.avancoPapelFinal) ?? 2;
      html += "<BR>".repeat(numeroAvancoPapel);

      const buffer = DriverEscPos.generateBufferFromHTML(html);

      return qz
        .print(config, [buffer])
        .then(() => {
          resolve(true);
        })
        .catch((error: any) => {
          console.log(error);
          if (retornarErro) {
            reject(error);
          }
        });
    });
  }

  getHtmlTesteImpressao(html: string) {
    html =
      "<BR>              <B>NOME DA LOJA</B><BR>            ENTREGA | 16686<BR><BR><B>CLIENTE: </B>JOAO PEDRO MACOTHE DA SILVA VAL<BR><B>REALIZADO: </B>23/05/2023 15:33:16<BR><B>CELULAR: </B>(11) 98462-3169<BR>----------------------------------------<BR>1X  ACAI COPO 300 ML COM SUCO SE   <B>12.00</B><BR>    ABACAXI<BR>1X  ACAI COPO 400 ML               <B>16.00</B>";
  }
}
